import React, {CSSProperties, ReactNode, useCallback, useState} from 'react';
import AntdTypography from 'antd/lib/typography';
import {extractDomProps} from '../../../utils/DomPropsExtractor';
import {EllipsisConfig} from '../text';
import classnames from 'classnames';
import {Tooltip} from '../tooltip';

/**
 * {@docCategory Title}
 */
export interface TitleProps {
   /**
    * The id of the element
    */
   id?: string;

   /**
    * Set content importance. Match with h1, h2, h3, h4, h5.
    * @defaultvalue 1
    */
   level?: 1 | 2 | 3 | 4 | 5;

   /**
    * Content type
    */
   type?: 'secondary' | 'success' | 'warning' | 'danger';

   /**
    * Strikethrough style
    */
   strikethrough?: boolean;

   /**
    * Underline style
    */
   underline?: boolean;

   /**
    * Code style (e.g. for doing code blocks)
    */
   code?: boolean;

   /**
    * Keyboard style (e.g. for documenting key strokes)
    */
   keyboard?: boolean;

   /**
    * Mark style (e.g. highlighter)
    */
   mark?: boolean;

   /**
    * Display ellipsis when text overflows
    */
   ellipsis?: boolean | EllipsisConfig;

   /**
    * Disabled content
    */
   disabled?: boolean;

   /**
    * Sets a className
    */
   className?: string;

   /**
    * Sets styles
    */
   style?: CSSProperties;

   /**
    * The children
    */
   children?: ReactNode;

   /**
    * The theme
    */
   theme?: 'light' | 'dark';
}

export const Title = (props: TitleProps) => {

   const { ellipsis } = props;
   const ellipsisIsObject = typeof ellipsis === 'object';

   const [isOverflow, setOverflow] = useState(false);
   const onEllipsis = useCallback((ellipsis: boolean) => {
      setOverflow(ellipsis);
      if (ellipsisIsObject && (ellipsis as any).onEllipsis) {
         (ellipsis as any).onEllipsis(ellipsis);
      }
   },[ellipsisIsObject]);

   const tooltip = (ellipsis && (ellipsis as any).tooltip && isOverflow);

   const ellipsisConfig = ellipsisIsObject ? {
      ...(ellipsis as any),
      onEllipsis: onEllipsis,
      tooltip: undefined
   } : ellipsis;

   const classNames = classnames('uf-title', {'uf-title-dark': props.theme === 'dark'}, props.className);

   return (
      tooltip ?
       <Tooltip title={props.children}>
          <AntdTypography.Title
             {...extractDomProps(props)}
             id={props.id}
             level={props.level}
             disabled={props.disabled}
             ellipsis={ellipsisConfig}
             type={props.type}
             underline={props.underline}
             delete={props.strikethrough}
             code={props.code}
             keyboard={props.keyboard}
             mark={props.mark}
             className={classNames}
             style={props.style}
          >
             {props.children}
          </AntdTypography.Title>
       </Tooltip>
       :
      <AntdTypography.Title
         {...extractDomProps(props)}
         id={props.id}
         level={props.level}
         disabled={props.disabled}
         ellipsis={ellipsisConfig}
         type={props.type}
         underline={props.underline}
         delete={props.strikethrough}
         code={props.code}
         keyboard={props.keyboard}
         mark={props.mark}
         className={classNames}
         style={props.style}
      >
         {props.children}
      </AntdTypography.Title>
   );
};
